/**
 * BlurZoneEditor — Canvas HTML5 pour dessiner des zones de floutage.
 * Sauve via PUT /api/media/{id}/blur (coordonnees relatives 0-1).
 *
 * Props :
 *   - media: object (avec original_cloudinary_url, privacy.blur_zones)
 *   - onSaved: (updatedMedia) => void
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { setMediaBlur } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Save, Trash2, RotateCcw } from "lucide-react";

const RGPD_STATUS = { compliant: "Conforme", pending: "À revoir", issue: "Non conforme" };

export default function BlurZoneEditor({ media, onSaved }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [zones, setZones] = useState(media?.privacy?.blur_zones || []);
  const [drawing, setDrawing] = useState(null); // {x0,y0,x1,y1}
  const [rgpdNote, setRgpdNote] = useState(media?.privacy?.rgpd_note || "");
  const [rgpdStatus, setRgpdStatus] = useState(media?.privacy?.rgpd_status || "compliant");
  const [saving, setSaving] = useState(false);

  const redraw = () => {
    const c = canvasRef.current;
    const img = imgRef.current;
    if (!c || !img) return;
    const ctx = c.getContext("2d");
    c.width = img.clientWidth;
    c.height = img.clientHeight;
    ctx.clearRect(0, 0, c.width, c.height);
    // Existing zones
    ctx.fillStyle = "rgba(255, 0, 0, 0.25)";
    ctx.strokeStyle = "rgba(255, 0, 0, 0.8)";
    ctx.lineWidth = 2;
    zones.forEach((z, i) => {
      ctx.fillRect(z.x * c.width, z.y * c.height, z.w * c.width, z.h * c.height);
      ctx.strokeRect(z.x * c.width, z.y * c.height, z.w * c.width, z.h * c.height);
      ctx.fillStyle = "rgba(0,0,0,0.7)";
      ctx.font = "12px sans-serif";
      ctx.fillText(`#${i + 1}`, z.x * c.width + 4, z.y * c.height + 14);
      ctx.fillStyle = "rgba(255, 0, 0, 0.25)";
    });
    // Drawing in progress
    if (drawing) {
      const x = Math.min(drawing.x0, drawing.x1);
      const y = Math.min(drawing.y0, drawing.y1);
      const w = Math.abs(drawing.x1 - drawing.x0);
      const h = Math.abs(drawing.y1 - drawing.y0);
      ctx.strokeStyle = "rgba(0,0,255,0.8)";
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    }
  };

  useEffect(() => {
    redraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zones, drawing]);

  useEffect(() => {
    const handler = () => redraw();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMouseDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDrawing({ x0: e.clientX - rect.left, y0: e.clientY - rect.top, x1: e.clientX - rect.left, y1: e.clientY - rect.top });
  };
  const onMouseMove = (e) => {
    if (!drawing) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setDrawing({ ...drawing, x1: e.clientX - rect.left, y1: e.clientY - rect.top });
  };
  const onMouseUp = () => {
    if (!drawing) return;
    const c = canvasRef.current;
    if (!c) return;
    const x = Math.min(drawing.x0, drawing.x1) / c.width;
    const y = Math.min(drawing.y0, drawing.y1) / c.height;
    const w = Math.abs(drawing.x1 - drawing.x0) / c.width;
    const h = Math.abs(drawing.y1 - drawing.y0) / c.height;
    if (w > 0.01 && h > 0.01) {
      setZones([...zones, { x, y, w, h, shape: "rect" }]);
    }
    setDrawing(null);
  };

  const removeZone = (i) => setZones(zones.filter((_, k) => k !== i));
  const resetAll = () => setZones([]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await setMediaBlur(media.id, {
        blur_zones: zones,
        rgpd_note: rgpdNote,
        rgpd_status: rgpdStatus,
      });
      toast.success(`${zones.length} zone(s) enregistrée(s)`);
      onSaved && onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="blur-zone-editor">
      <div className="relative inline-block max-w-full">
        <img
          ref={imgRef}
          src={media.original_cloudinary_url}
          alt=""
          onLoad={redraw}
          crossOrigin="anonymous"
          className="max-w-full max-h-[60vh] rounded border select-none pointer-events-none"
          data-testid="blur-image"
        />
        <canvas
          ref={canvasRef}
          className="absolute inset-0 cursor-crosshair"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={() => setDrawing(null)}
          data-testid="blur-canvas"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-sm font-semibold mb-2">Zones ({zones.length})</div>
          {zones.length === 0 ? (
            <p className="text-xs text-muted-foreground">Cliquer-glisser sur l'image pour dessiner une zone.</p>
          ) : (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {zones.map((z, i) => (
                <div key={i} className="flex items-center gap-2 text-xs bg-secondary p-2 rounded">
                  <span className="font-mono">#{i + 1}</span>
                  <span className="text-muted-foreground">{(z.w * 100).toFixed(0)}×{(z.h * 100).toFixed(0)}%</span>
                  <button onClick={() => removeZone(i)} className="ml-auto"><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">Statut RGPD</label>
          <Select value={rgpdStatus} onValueChange={setRgpdStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(RGPD_STATUS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="block text-xs font-medium mb-1 mt-3">Note RGPD</label>
          <Input value={rgpdNote} onChange={(e) => setRgpdNote(e.target.value)} placeholder="Contexte, personnes concernées..." />
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} className="gap-2" data-testid="btn-save-blur">
          <Save className="h-4 w-4" /> {saving ? "..." : "Enregistrer les zones"}
        </Button>
        <Button variant="outline" onClick={resetAll} disabled={zones.length === 0} className="gap-2">
          <RotateCcw className="h-4 w-4" /> Réinitialiser
        </Button>
      </div>
    </div>
  );
}
